import ActionType from '@/Common/Enum/ActionType.js';
import nightcrawler, {
  NIGHTCRAWLER_CHECK_STATUS,
  onlyFailing
} from '@/Common/Classifiers/NightcrawlerCheckClassifier.js';
import i18n from '@/Plugins/i18n.js';

const t = (key, params) =>
  i18n.global.t(`common.dmarc_directive.${key}`, params);

export default nightcrawler({
  _root: [
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DMARC001':
              return t('dmarc001');
            case 'DMARC002':
              return t('dmarc002');
            case 'DMARC003':
              return t('dmarc003');
            case 'DMARC004':
              return t('dmarc004');
            case 'DMARC005':
            default:
              return c.msg;
          }
        })
    }
  ],
  v: [
    {
      default: true,
      required: true,
      actionType: ActionType.ACTION_REQUIRED,
      message: t('v_not_specified'),
      errorMessages: [t('v_first_not_dmarc1')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('v_first_not_dmarc1')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  p: [
    {
      default: true,
      required: true,
      actionType: ActionType.ACTION_REQUIRED,
      message: t('p_not_specified')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('p_should_be_valid')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      value: ['none', 'quarantine', 'reject'],
      actionType: ActionType.NO_ACTION
    }
  ],
  sp: [
    {
      default: true,
      required: false,
      actionType: ActionType.NO_ACTION_WITH_DEFAULT,
      message: t('sp_not_specified')
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('sp_should_be_valid')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      value: ['none', 'quarantine', 'reject'],
      actionType: ActionType.NO_ACTION
    }
  ],
  pct: [
    {
      default: true,
      required: false,
      message: t('pct_default'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED,
      errorMessages: [t('pct_invalid_percentage')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.WARN,
      actionType: ActionType.ACTION_RECOMMENDED,
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((check) => check.msg)
    }
  ],
  rua: [
    {
      default: true,
      required: false,
      message: t('rua_not_specified'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map(({ msg_code, msg }) => {
          switch (msg_code) {
            case 'DMARCRUA01':
              return t('rua_comma_separated');
            case 'DMARCRUA02':
              return t('rua_delivery_method_unknown');
            case 'DMARCRUA03':
              return t('rua_invalid_emails');
            case 'DMARCRUA04':
              return t('rua_invalid_size_specifiers');
            case 'DMARCRUA05':
              return t('rua_invalid_mx_records');
            case 'DMARCRUA06':
              return t('rua_external_domains_no_permission');
            default:
              return msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  ruf: [
    {
      default: true,
      required: false,
      message: t('ruf_not_specified'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map(({ msg_code, msg }) => {
          switch (msg_code) {
            case 'DMARCRUF01':
              return t('ruf_comma_separated');
            case 'DMARCRUF02':
              return t('ruf_delivery_method_unknown');
            case 'DMARCRUF03':
              return t('ruf_invalid_emails');
            case 'DMARCRUF04':
              return t('ruf_invalid_size_specifiers');
            case 'DMARCRUF05':
              return t('ruf_invalid_mx_records');
            case 'DMARCRUF06':
              return t('ruf_external_domains_no_permission');
            default:
              return msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  fo: [
    {
      default: true,
      required: false,
      message: t('fo_default'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DMARCFO01':
              return t('fo_invalid_colon_delimited');
            case 'DMARCFO02':
            default:
              return c.msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION,
      message: ({ value }) => {
        return value
          .split(':')
          .map((d) => {
            switch (d) {
              case '0':
                return t('fo_0');
              case '1':
                return t('fo_1');
              case 'd':
                return t('fo_d');
              case 's':
                return t('fo_s');
            }
          })
          .join('\n');
      }
    }
  ],
  rf: [
    {
      default: true,
      required: false,
      message: t('rf_not_specified'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION,
      errorMessages: [t('rf_should_be_afrf')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      message: ({ value }) => t('rf_invalid_format', { value }),
      actionType: ActionType.ACTION_REQUIRED
    }
  ],
  ri: [
    {
      default: true,
      required: false,
      message: t('ri_default'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: [NIGHTCRAWLER_CHECK_STATUS.WARN, NIGHTCRAWLER_CHECK_STATUS.ERROR],
      actionType: ({ status }) =>
        ({
          [NIGHTCRAWLER_CHECK_STATUS.WARN]: ActionType.ACTION_RECOMMENDED,
          [NIGHTCRAWLER_CHECK_STATUS.ERROR]: ActionType.ACTION_REQUIRED
        })[status],
      errorMessages: ({ checks }) =>
        onlyFailing(checks).map((c) => {
          switch (c.msg_code) {
            case 'DMARCRI01':
              return t('ri_invalid_integer');
            case 'DMARCRI02':
              return t('ri_best_effort');
            default:
              return c.msg;
          }
        })
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION
    }
  ],
  aspf: [
    {
      value: null,
      default: true,
      required: false,
      message: t('aspf_default'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION,
      errorMessages: [t('aspf_not_r_or_s')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED
    }
  ],
  adkim: [
    {
      default: true,
      required: false,
      message: t('adkim_default'),
      actionType: ActionType.NO_ACTION_WITH_DEFAULT
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.OK,
      actionType: ActionType.NO_ACTION,
      errorMessages: [t('adkim_not_r_or_s')]
    },
    {
      status: NIGHTCRAWLER_CHECK_STATUS.ERROR,
      actionType: ActionType.ACTION_REQUIRED
    }
  ]
});
